import { useState } from "react";
import { Play, ExternalLink, GraduationCap, Info, Youtube } from "lucide-react";

const VIDEO_ID = "jTp9hyg23Mw";
const VIDEO_URL = `https://www.youtube.com/watch?v=${VIDEO_ID}`;
const POSTER_URL = `https://i.ytimg.com/vi/${VIDEO_ID}/maxresdefault.jpg`;
const POSTER_FALLBACK = `https://i.ytimg.com/vi/${VIDEO_ID}/hqdefault.jpg`;

export const EducationalVideo = () => {
  const [posterSrc, setPosterSrc] = useState(POSTER_URL);

  return (
    <section
      id="learn"
      data-testid="educational-video-section"
      className="relative py-20 md:py-28 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-slate-50 to-white" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(6,182,212,0.06),transparent_60%)]" />

      <div className="relative max-w-5xl mx-auto px-6 md:px-12">
        {/* Heading */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-100 border border-cyan-200 mb-5">
            <GraduationCap className="w-4 h-4 text-cyan-600" />
            <span className="text-cyan-700 text-sm font-semibold tracking-wide">Learn · Awareness</span>
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-slate-800 mb-4">
            Know Your <span className="gradient-text">Kidney Numbers</span>
          </h2>
          <p className="text-slate-600 text-base md:text-lg leading-relaxed">
            Two simple tests — <span className="text-slate-800 font-semibold">uACR</span> (urine) and
            <span className="text-slate-800 font-semibold"> eGFR</span> (blood) — can reveal kidney disease
            years before symptoms appear. Watch this short video by the National Kidney Foundation to
            understand what these numbers mean and why they matter.
          </p>
        </div>

        {/* Video Card — Opens on YouTube (embed disabled by uploader) */}
        <a
          href={VIDEO_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="educational-video-card"
          aria-label="Watch video on YouTube: Know Your Kidney Numbers"
          className="group block relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl border border-slate-200 bg-slate-900 aspect-video"
        >
          <img
            src={posterSrc}
            alt="Video preview - Know Your Kidney Numbers: uACR & eGFR Explained"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={() => setPosterSrc(POSTER_FALLBACK)}
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />

          {/* Play Button */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-white/30 animate-ping" />
              <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/95 shadow-2xl flex items-center justify-center group-hover:scale-110 group-hover:bg-white transition-all duration-300">
                <Play className="w-8 h-8 md:w-10 md:h-10 text-cyan-600 ml-1.5" fill="currentColor" />
              </div>
            </div>
          </div>

          {/* Top-right badge */}
          <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10">
            <Youtube className="w-3.5 h-3.5 text-red-500" fill="currentColor" />
            <span className="text-white text-xs font-medium">Watch on YouTube</span>
            <ExternalLink className="w-3 h-3 text-white/70" />
          </div>

          {/* Bottom caption */}
          <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 mb-2">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-white text-xs font-semibold tracking-wide">EDUCATIONAL</span>
            </div>
            <p className="text-white font-display text-lg md:text-2xl font-semibold drop-shadow-lg">
              Know Your Kidney Numbers: uACR &amp; eGFR Explained
            </p>
            <p className="text-white/70 text-xs md:text-sm mt-1 drop-shadow">
              by National Kidney Foundation
            </p>
          </div>
        </a>

        {/* Attribution + Source */}
        <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 md:p-5 rounded-2xl bg-slate-50 border border-slate-200">
          <div className="flex items-start gap-3 text-sm text-slate-600">
            <Info className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <p>
              This educational video is shared for public awareness. Related reading:{" "}
              <a
                href="https://www.kidney.org/news-stories/why-early-detection-key-to-better-kidney-health"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nkf-reference-link"
                className="text-cyan-600 hover:text-cyan-700 hover:underline font-medium"
              >
                National Kidney Foundation — Why Early Detection is Key
              </a>
              .
            </p>
          </div>
          <a
            href={VIDEO_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="youtube-source-link"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-cyan-600 transition-colors flex-shrink-0 whitespace-nowrap"
          >
            <span>Watch on YouTube</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
};
